import { Component, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { IStoredUser, PORTAL_NAME, TokenStorageService } from '@dlap-ui/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { CreateTicketPayload } from '../tickets-api/tickets-api.models';
import { TicketsService } from '../tickets-api/tickets.service';
import { ReportIssueContext, toTicketPayloadRecord } from '../report-issue/report-issue.model';

export const LODGE_TICKET_MAX_LENGTH = 150;

@Component({
  selector: 'dlap-lodge-ticket-modal',
  imports: [ReactiveFormsModule, NzModalModule, NzButtonModule, NzInputModule, NzAlertModule],
  templateUrl: './lodge-ticket-modal.html',
})
export class LodgeTicketModal {
  private readonly ticketsApi = inject(TicketsService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly message = inject(NzMessageService);
  private readonly portal = inject(PORTAL_NAME, { optional: true }) ?? 'DLAP';

  readonly visible = model(false);
  readonly context = input<ReportIssueContext | null>(null);
  readonly created = output<void>();

  readonly maxLength = LODGE_TICKET_MAX_LENGTH;
  readonly isSubmitting = signal(false);
  readonly description = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(LODGE_TICKET_MAX_LENGTH)],
  });

  constructor() {
    effect(() => {
      const open = this.visible();
      const report = this.context();

      if (open && report) {
        untracked(() => this.description.setValue(report.message.slice(0, LODGE_TICKET_MAX_LENGTH)));
      }
    });
  }

  close(): void {
    if (this.isSubmitting()) {
      return;
    }

    this.visible.set(false);
    this.description.reset('');
  }

  submit(): void {
    const description = this.description.value.trim();

    if (!description || this.description.invalid) {
      this.description.markAsTouched();
      return;
    }

    if (this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    this.ticketsApi.createTicket(this.buildPayload(description)).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.message.success('Ticket lodged successfully.');
        this.visible.set(false);
        this.description.reset('');
        this.created.emit();
      },
      error: () => {
        this.isSubmitting.set(false);
        this.message.error('Unable to lodge your ticket. Please try again.');
      },
    });
  }

  private buildPayload(description: string): CreateTicketPayload {
    const user = this.tokenStorage.getUser() as IStoredUser | null;
    const report = this.context();

    return {
      userId: user?.id,
      username: user?.username ?? user?.email,
      portal: this.portal,
      subject: report?.title ?? 'User reported issue',
      description,
      roles: user?.roles ?? [],
      viewport: {
        width: String(window.innerWidth),
        height: String(window.innerHeight),
      },
      metadata: {
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        ...(report?.metadata ?? {}),
        ...(report?.applicationReference ? { applicationReference: report.applicationReference } : {}),
        ...(report?.applicationType ? { applicationType: report.applicationType } : {}),
        ...(report ? { errorMessage: report.message } : {}),
      },
      requestPayload: toTicketPayloadRecord(report?.requestPayload),
      responseBody: toTicketPayloadRecord(report?.responseBody),
    };
  }
}
